import { useCallback, useContext } from 'react';
import { Download, Loader2 } from 'lucide-react';
import * as Sentry from '@sentry/react';

import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/notifications';
import { AuthContext } from '@/context/auth-context';
import { useOrgStore } from '@/stores/org-store';
import { useExportSearchQueries } from '@/hooks/mutations/external-chat/use-external-chat-mutations';
import { ExternalChatApiError } from '@/services/external-chat/detail-error';
import { downloadCsv, promptExportFilename } from '@/utils/download-csv';
import { defaultSearchQueryRange } from '../helpers/search-query-export';

export function SearchQueryExportSection({
  orgId,
  collectionId,
}: {
  orgId: string;
  collectionId: string;
}) {
  const notification = useNotification();
  const { user, isAdmin } = useContext(AuthContext);
  const { currentOrg, orgs } = useOrgStore();
  const canExport = isAdmin || !!user?.isOwner;
  const exportQueries = useExportSearchQueries();

  const reportError = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ExternalChatApiError) {
        notification.error(err.message);
        return;
      }
      Sentry.captureException(err, {
        extra: { context: 'export-search-queries', collectionId },
      });
      notification.error(fallback);
    },
    [collectionId, notification],
  );

  const handleDownload = useCallback(async () => {
    const range = defaultSearchQueryRange(new Date());
    try {
      const csv = await exportQueries.mutateAsync({
        orgId,
        collectionId,
        from: range.from,
        to: range.to,
      });
      const matchedName = orgs.find((org) => org.orgid === orgId)?.name;
      const currentName =
        currentOrg?.orgid === orgId ? currentOrg.name : undefined;
      const orgName = matchedName || currentName || orgId;
      downloadCsv(
        csv,
        promptExportFilename(orgName, collectionId, range.from, range.to),
      );
    } catch (error) {
      reportError(error, 'Could not download search queries');
    }
  }, [
    collectionId,
    currentOrg?.name,
    currentOrg?.orgid,
    exportQueries,
    orgId,
    orgs,
    reportError,
  ]);

  if (!canExport) return null;

  return (
    <section className='space-y-3 rounded-lg border px-3 py-4'>
      <h3 className='text-sm font-semibold'>Search queries</h3>
      <p className='text-xs text-muted-foreground'>
        Last 30 days of search queries for this collection.
      </p>
      <Button
        variant='outline'
        size='sm'
        onClick={handleDownload}
        disabled={exportQueries.isPending}
      >
        {exportQueries.isPending ? (
          <Loader2 className='size-3.5 mr-1 animate-spin' />
        ) : (
          <Download className='size-3.5 mr-1' />
        )}
        Download CSV
      </Button>
    </section>
  );
}
